import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, ViewEncapsulation, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import {
    SwitchSelectionDialogComponent,
    SwitchSelectionDialogData,
} from './switch-selection-dialog/switch-selection-dialog.component';

@Component({
    selector: 'company-switcher',
    templateUrl: './company-switcher.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
    ],
    styles: [`
        :host {
            display: block;
            width: 100%;
            max-width: 100%;
        }

        .company-context-badge {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            max-width: 100%;
            height: 40px;
            padding: 0 12px;
            border-radius: 8px;
            border: 1px solid rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.28);
            background: linear-gradient(
                135deg,
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.16),
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.08)
            );
            transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
            user-select: none;
        }

        .company-context-badge--interactive {
            cursor: pointer;
        }

        .company-context-badge--interactive:hover {
            border-color: rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.5);
            background: linear-gradient(
                135deg,
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.22),
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.12)
            );
            box-shadow: 0 4px 14px rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.18);
            transform: translateY(-1px);
        }

        .company-context-badge--interactive:focus-visible {
            outline: 2px solid rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.6);
            outline-offset: 2px;
        }

        .company-context-badge__text {
            display: flex;
            flex-direction: column;
            justify-content: center;
            flex: 1 1 auto;
            min-width: 0;
            line-height: 1.1;
        }

        .company-context-badge__company {
            font-size: 13px;
            font-weight: 600;
            color: var(--fuse-text-default, #1e293b);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .company-context-badge__branch {
            display: flex;
            align-items: center;
            gap: 2px;
            margin-top: 1px;
            font-size: 11px;
            font-weight: 500;
            color: var(--fuse-text-secondary, #64748b);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .company-context-badge__branch-icon {
            flex: 0 0 auto;
            opacity: 0.75;
        }

        .company-context-badge__chevron {
            flex: 0 0 auto;
            color: var(--fuse-text-secondary, #64748b);
        }

        .company-context-badge__chevron--spin {
            animation: company-switcher-spin 0.9s linear infinite;
        }

        @keyframes company-switcher-spin {
            to { transform: rotate(360deg); }
        }

        .dark .company-context-badge {
            border-color: rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.35);
            background: linear-gradient(
                135deg,
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.24),
                rgba(var(--fuse-primary-rgb, 1, 126, 132), 0.14)
            );
        }

        .dark .company-context-badge__company {
            color: #f1f5f9;
        }

        .dark .company-context-badge__branch,
        .dark .company-context-badge__chevron {
            color: #94a3b8;
        }

        .company-switcher-embedded {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .company-switcher-embedded__action {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-height: 36px;
            padding: 0 8px;
            border: 0;
            border-radius: 8px;
            background: transparent;
            color: inherit;
            font: inherit;
            font-size: 13px;
            font-weight: 500;
            text-align: left;
            cursor: pointer;
        }

        .company-switcher-embedded__action:hover:not(:disabled) {
            background: rgba(15, 23, 42, 0.06);
        }

        .company-switcher-embedded__action:disabled {
            opacity: 0.55;
            cursor: default;
        }

        .company-switcher-embedded__action-icon {
            color: var(--fuse-text-secondary, #64748b);
        }

        .dark .company-switcher-embedded__action-icon {
            color: #94a3b8;
        }

        .dark .company-switcher-embedded__action:hover:not(:disabled) {
            background: rgba(248, 250, 252, 0.08);
        }
    `],
})
export class CompanySwitcherComponent {
  private readonly _destroyRef = inject(DestroyRef);
    @Input() embedded = false;
    switching = false;

    constructor(
        private _authService: AuthService,
        private _dialog: MatDialog,
        private _router: Router,
        private _toast: ToastrService
    ) {}

    /** Show the switch-company option only when login flagged multiple companies. */
    get canSwitchCompany(): boolean {
        return this._authService.canSwitchCompany();
    }

    /** Show the switch-branch option only when login/select-company flagged multiple branches. */
    get canSwitchBranch(): boolean {
        return this._authService.canSwitchBranch();
    }

    /** True when at least one switch option is available (enables the dropdown). */
    get hasAnyOption(): boolean {
        return this.canSwitchCompany || this.canSwitchBranch;
    }

    /** Name of the company the user is currently working in. */
    get currentCompanyName(): string | null {
        return this._authService.getCurrentCompanyName();
    }

    /** Name of the branch the user is currently working in. */
    get currentBranchName(): string | null {
        return this._authService.getCurrentBranchName();
    }

    /** True when we have at least a company name to show the badge. */
    get hasContext(): boolean {
        return !!this.currentCompanyName || !!this.currentBranchName;
    }

    openCompanySwitch(): void {
        const currentId = this._authService.getCurrentCompanyId();
        const data: SwitchSelectionDialogData = {
            mode: 'company',
            title: 'Switch Company',
            items: this._authService.getAvailableCompanies(),
            currentId,
        };

        this._dialog
            .open(SwitchSelectionDialogComponent, {
                width: '480px',
                maxWidth: '95vw',
                autoFocus: false,
                panelClass: 'bp-documents-dialog-panel',
                data,
            })
            .afterClosed()
            .pipe(takeUntilDestroyed(this._destroyRef)).subscribe((selectedId: string | undefined) => {
                if (!selectedId || selectedId === currentId) {
                    return;
                }
                this._switchCompany(selectedId);
            });
    }

    openBranchSwitch(): void {
        const currentId = this._authService.getCurrentBranchId();
        const data: SwitchSelectionDialogData = {
            mode: 'branch',
            title: 'Switch Branch',
            items: this._authService.getAvailableBranches(),
            currentId,
        };

        this._dialog
            .open(SwitchSelectionDialogComponent, {
                width: '480px',
                maxWidth: '95vw',
                autoFocus: false,
                panelClass: 'bp-documents-dialog-panel',
                data,
            })
            .afterClosed()
            .pipe(takeUntilDestroyed(this._destroyRef)).subscribe((selectedId: string | undefined) => {
                if (!selectedId || selectedId === currentId) {
                    return;
                }
                this._switchBranch(selectedId);
            });
    }

    private _switchCompany(companyId: string): void {
        this.switching = true;
        lastValueFrom(this._authService.selectCompany(companyId))
            .then((resp: any) => {
                const responseData = resp?.data ?? resp;
                const requiresBranchSelection =
                    responseData?.requiresBranchSelection === true ||
                    responseData?.requiresBranchSelection === 'true';

                // Newly selected company still needs a branch chosen: reuse the
                // existing branch-selection page (reads availableBranches from storage).
                if (requiresBranchSelection) {
                    this.switching = false;
                    sessionStorage.setItem('loginResponse', JSON.stringify(resp));
                    this._router.navigate(['/select-branch']);
                    return;
                }

                lastValueFrom(this._authService.completeLogin())
                    .then(() => {
                        this._toast.success('Company switched successfully');
                        this._reloadIntoApp();
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.switching = false;
                        this._toast.error(err?.error?.message || 'Failed to load profile');
                    });
            })
            .catch((err: HttpErrorResponse) => {
                this.switching = false;
                this._toast.error(err?.error?.message || 'Failed to switch company');
            });
    }

    private _switchBranch(branchId: string): void {
        this.switching = true;
        lastValueFrom(this._authService.selectBranch(branchId))
            .then(() => {
                this._toast.success('Branch switched successfully');
                this._reloadIntoApp();
            })
            .catch((err: HttpErrorResponse) => {
                this.switching = false;
                this._toast.error(err?.error?.message || 'Failed to switch branch');
            });
    }

    /** Reload the app so navigation, permissions and modules reflect the new context. */
    private _reloadIntoApp(): void {
        this._router.navigateByUrl('/signed-in-redirect').then(() => {
            window.location.reload();
        });
    }
}
